import { useEffect, useState, useRef } from 'react';
import { useCallingData } from '../components/Context/CallingContext';

// Format the time as HH:MM:SS
const formatTime = (seconds) => {
  const hours = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const sec = String(seconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${sec}`;
};

const Timer = ({ duration, setDuration }) => {
  const { resetTimer, setResetTimer } = useCallingData();
  const [startTime, setStartTime] = useState(null);
  const durationRef = useRef(duration);
  const startTimeRef = useRef(startTime);

  // Ensure duration and startTime are correctly synced to refs
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  useEffect(() => {
    startTimeRef.current = startTime;
  }, [startTime]);

  useEffect(() => {
    // Only fetch from localStorage when the component mounts or duration is 0
    if (duration === 0 && !resetTimer) {
      const savedDuration = localStorage.getItem('timerDuration');
      const savedStartTime = localStorage.getItem('timerStartTime');

      // If values are available, set them
      if (savedDuration && savedStartTime) {
        setDuration(Number(savedDuration));
        setStartTime(Number(savedStartTime));
      } else {
        // Otherwise initialize a new timer
        const currentTime = Date.now();
        setStartTime(currentTime);
      }
    }

    if (startTime !== null) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(elapsed); // Update duration state

        // Persist duration and startTime to localStorage
        localStorage.setItem('timerStartTime', startTimeRef.current);
        localStorage.setItem('timerDuration', elapsed.toString());
      }, 1000);

      // Clean up interval on component unmount
      return () => clearInterval(interval);
    }
  }, [startTime, duration, setDuration, resetTimer]);

  useEffect(() => {
    if (resetTimer) {
      // Clear timer data from localStorage
      localStorage.removeItem('timerStartTime');
      localStorage.removeItem('timerDuration');
      setDuration(0); // Reset duration to 0
      setStartTime(Date.now()); // Set new start time to now

      setTimeout(() => {
        // Reset the resetTimer flag and update localStorage with new start time
        setResetTimer(false);
        localStorage.setItem('timerStartTime', Date.now().toString());
        localStorage.setItem('timerDuration', '0');
      }, 100); // Wait for a brief moment before resetting the resetTimer state
    }
  }, [resetTimer, setDuration, setResetTimer]);

  return formatTime(duration);
};

export default Timer;
